import * as Popover from '@radix-ui/react-popover';
import { useRef, useState, useEffect } from 'react';
import { ALIGNMENT } from 'ui/selects';
import { createDropdownToggle } from 'ui/selects/utils';
import { useDropdownPopoverAlignment } from 'ui/utility/hooks/useDropdownPopoverAlignment';
import { SecondaryButton } from 'ui/button';
import { HORIZONTAL_ALIGNMENT, VERTICAL_ALIGNMENT } from 'ui/utility/types';
import noop from 'utils/noop';
import { DateType, DateRangePickerProps } from './types';
import { DateAndCalendarWrapper, RowWrapper } from './DatePicker.styles';
import { DATE_PICKER_PORTAL_ID } from './constants';
import { CollapisbleCalendarDateRangePicker } from './DateRangePickerWithCollapsibleCalendar.styles';
import { RELATIVE_DATE_PRESETS } from './helpers';

export type DateRangePickerWithCollapsibleCalendarProps = DateRangePickerProps & {
  id?: string;
  /** horizontal alignment of calender dropdown */
  horizontalAlignment?: ALIGNMENT.LEFT | ALIGNMENT.RIGHT;
  /** vertical alignment of calender dropdown */
  verticalAlignment?: ALIGNMENT.DOWN | ALIGNMENT.UP;
  /**
   * Automatically change the dropdown `verticalAlignment` and `horizontalAlignment`
   * to avoid collisions with the viewport.
   * When this is on, `verticalAlignment` and `horizontalAlignment` are used as default alignments.
   */
  automaticallyAvoidCollisions?: boolean;
  isDisabled?: boolean;
  placeholder?: string;
  onClose?: () => void;
  /** Wheter to close dropdown after onCancel or OnSubmit */
  closeDropdownOnFooterActions?: boolean;
};

export const DateRangePickerWithCollapsibleCalendar = ({
  id,
  verticalAlignment = ALIGNMENT.DOWN,
  horizontalAlignment = ALIGNMENT.LEFT,
  automaticallyAvoidCollisions = false,
  isDisabled = false,
  placeholder = '--',
  onClose = noop,
  startDate,
  endDate,
  promoteCustomInput,
  dateRangePreset = 'CUSTOM_RANGE',
  presets,
  onDateRangeChange,
  onPresetChange,
  validDateRange,
  blockFutureDates,
  blockPastDates,
  showRelativeDatePresets,
  defaultTimeZone,
  hasAllTimePreset,
  onTimeZoneChange,
  hasActions,
  cancelLabel,
  primaryActionLabel,
  onCancel,
  onSubmit,
  closeDropdownOnFooterActions = false,
  areTimePickersDisabled,
  isTimeZonePickerDisabled,
  hideTimeInputs,
  hideTimeZonePicker,
  customCalendarTileContent,
  useLocalTimeZone,
  timezoneHorizontalAlignment,
  ...props
}: DateRangePickerWithCollapsibleCalendarProps) => {
  const parentEl = useRef(null);

  const [isCalendarOpen, setIsCalendarOpen] = useState(false);

  const toggleDropdown = createDropdownToggle(setIsCalendarOpen, isCalendarOpen);

  useEffect(() => {
    if (!isCalendarOpen) {
      onClose();
    }
  }, [isCalendarOpen]);

  const getTimeText = (start: DateType, end: DateType, preset?: string) => {
    if (!start || !end) {
      return placeholder;
    }
    const format = hideTimeInputs ? 'MM/DD/YY' : 'MM/DD/YY hh:mm';
    if (preset === 'CUSTOM_RANGE') {
      return `between ${start?.format(format)} - ${end?.format(format)}`;
    }
    // @ts-ignore
    return RELATIVE_DATE_PRESETS[preset].filterLabel;
  };
  const { popoverVerticalAlignment, popoverHorizontalAlignment } = useDropdownPopoverAlignment(
    horizontalAlignment as HORIZONTAL_ALIGNMENT,
    verticalAlignment as VERTICAL_ALIGNMENT,
  );

  const handleCancel = () => {
    onCancel?.();
    closeDropdownOnFooterActions && setIsCalendarOpen(false);
  };

  const handleSubmit = () => {
    onSubmit?.();
    closeDropdownOnFooterActions && setIsCalendarOpen(false);
  };

  return (
    <Popover.Root open={isCalendarOpen} onOpenChange={toggleDropdown}>
      <DateAndCalendarWrapper ref={parentEl} id={id} {...props}>
        <RowWrapper>
          <Popover.Trigger
            asChild
            onClick={(e) => {
              e.preventDefault();
              if (!isDisabled) {
                toggleDropdown();
              }
            }}
          >
            <SecondaryButton onClick={noop} data-test="date-range-picker-trigger">
              {getTimeText(startDate, endDate, dateRangePreset)}
            </SecondaryButton>
          </Popover.Trigger>
        </RowWrapper>
        <Popover.Portal>
          <Popover.Content
            asChild
            data-portal-id={DATE_PICKER_PORTAL_ID}
            avoidCollisions={automaticallyAvoidCollisions}
            side={popoverVerticalAlignment}
            align={popoverHorizontalAlignment}
            sideOffset={4} // space between the dropdown and the dropdown trigger
          >
            <CollapisbleCalendarDateRangePicker
              data-test="date-range-picker"
              startDate={startDate}
              endDate={endDate}
              promoteCustomInput={promoteCustomInput}
              dateRangePreset={dateRangePreset}
              presets={presets}
              onDateRangeChange={onDateRangeChange}
              onPresetChange={onPresetChange}
              validDateRange={validDateRange}
              blockFutureDates={blockFutureDates}
              blockPastDates={blockPastDates}
              showRelativeDatePresets={showRelativeDatePresets}
              defaultTimeZone={defaultTimeZone}
              hasAllTimePreset={hasAllTimePreset}
              onTimeZoneChange={onTimeZoneChange}
              hasActions={hasActions}
              cancelLabel={cancelLabel}
              primaryActionLabel={primaryActionLabel}
              onCancel={handleCancel}
              onSubmit={handleSubmit}
              areTimePickersDisabled={areTimePickersDisabled}
              isTimeZonePickerDisabled={isTimeZonePickerDisabled}
              hideTimeInputs={hideTimeInputs}
              hideTimeZonePicker={hideTimeZonePicker}
              customCalendarTileContent={customCalendarTileContent}
              useLocalTimeZone={useLocalTimeZone}
              timezoneHorizontalAlignment={timezoneHorizontalAlignment}
            />
          </Popover.Content>
        </Popover.Portal>
      </DateAndCalendarWrapper>
    </Popover.Root>
  );
};
